import React, { useEffect } from 'react';
import styled from 'styled-components';
import Icon from 'components/Icon';
import { POSIconButton } from 'components/POS';
import { useCart } from 'shared/CartContext';
import { useGetGATickets } from './gql/queries/useGetGATickets';
import SeatsManagerModal from './SeatsManager';

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  padding: 24px;
  overflow: hidden;

  @media (max-width: 1024px) {
    flex: 1 1 auto;
    min-height: 300px;
    padding: 16px;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  overflow-x: auto;
  padding-bottom: 4px;
  flex-shrink: 0;

  /* Hide scrollbar but allow scrolling */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${(props) =>
    props.selected ? props.theme.colors?.primary || '#FA4616' : 'transparent'};
  border: ${(props) =>
    props.selected
      ? `2px solid ${props.theme.colors?.primary || '#FA4616'}`
      : props.theme.name === 'dark'
      ? '2px solid rgba(255, 255, 255, 0.2)'
      : '2px solid rgba(0, 0, 0, 0.15)'};
  border-radius: 8px;
  color: ${(props) =>
    props.selected
      ? '#ffffff'
      : props.theme.name === 'dark'
      ? props.theme.colors?.primary || '#FA4616'
      : '#64748B'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  font-family: 'Barlow Condensed', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    border-color: ${(props) => props.theme.colors?.primary || '#FA4616'};
    color: ${(props) =>
      props.selected ? '#ffffff' : props.theme.colors?.primary || '#FA4616'};
  }
`;

const TabIcon = styled.img`
  width: 24px;
  height: 24px;
  filter: ${(props) =>
    props.selected
      ? 'brightness(0) saturate(100%) invert(100%)' // White
      : 'brightness(0) saturate(100%) invert(42%) sepia(96%) saturate(1352%) hue-rotate(349deg) brightness(97%) contrast(105%)'}; // Orange
`;

const TicketListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 12px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.muted || '#64748B'
      : '#64748B'};
`;

const TicketsContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;

  /* Hide scrollbar completely - content still scrollable */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1024px) {
    flex: 1 1 auto;
    min-height: 200px;
  }
`;

const TicketItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 20px;
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.overlay || 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? props.theme.colors?.['border-light'] || 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const TicketName = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const TicketPrice = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.colors?.primary || '#FA4616'};
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  padding: 4px;
  border-radius: 8px;
`;

const QtyDisplay = styled.span`
  font-size: 16px;
  font-weight: 600;
  min-width: 32px;
  text-align: center;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const MobileRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const DesktopControls = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const TicketListV2 = () => {
  const {
    tickets,
    setTickets,
    setTypes,
    seatsModalIsVisible,
    setSeatsModalIsVisible,
    setTicketCount,
    selected,
    setSelected,
    chartRef,
    addTicket,
    removeTicket,
  } = useCart();

  const { data, loading } = useGetGATickets();

  useEffect(() => {
    if (!loading && data?.getGATicketsV2) {
      // Reset the values of the tickets if the ticket length is 0
      if (data?.getGATicketsV2.length === 0) {
        setTickets([]);
        setTypes([]);
        setTicketCount(0);
        setSelected(null);
        return;
      }

      // Compare the saved tickets and the tickets from the API based on id and price
      const savedTickets = localStorage.getItem(`tickets`);
      const storedTickets = JSON.parse(savedTickets);

      let ticketCount = 0;

      const ticketWithCount = Object.keys(data.getGATicketsV2)?.reduce(
        (acc, key) => {
          setTypes((types) =>
            types?.length > 0
              ? types.concat(data.getGATicketsV2[key]?.type)
              : [data.getGATicketsV2[key]?.type]
          );

          return {
            ...acc,
            [key]: {
              ...data.getGATicketsV2[key],
              tickets: data.getGATicketsV2[key]?.tickets?.map((ticket) => {
                const savedTicket =
                  storedTickets &&
                  storedTickets[key]?.tickets?.find(
                    (t) =>
                      t.id === ticket.id && t.track_price === ticket.track_price
                  );

                if (savedTicket) ticketCount += savedTicket.count;

                return {
                  ...ticket,
                  price: ticket.track_price ? ticket.track_price : ticket.price,
                  count: savedTicket ? savedTicket.count : 0,
                  ...(ticket.reserved_seat
                    ? { seats: savedTicket?.seats }
                    : null),
                };
              }),
            },
          };
        },
        {}
      );

      setTicketCount(ticketCount);
      setTickets(ticketWithCount);

      // Set the selected ticket to the first ticket in the list
      setSelected(Object.keys(ticketWithCount)[0]);
    }
  }, [
    data,
    loading,
    setSelected,
    setTicketCount,
    setTickets,
    setTypes,
    tickets.reserved_seat,
  ]);

  useEffect(() => {
    if (seatsModalIsVisible) {
      if (chartRef.current?.chart) chartRef.current.chart.resetView();

      setSeatsModalIsVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef]);

  const handleAddTicket = (ticket) => {
    localStorage.setItem('selectedTicket', ticket.id);
    if (ticket?.reserved_seat) {
      setSeatsModalIsVisible(true);
    } else {
      addTicket(ticket);
    }
  };

  const handleRemoveTicket = (ticket) => {
    localStorage.setItem('selectedTicket', ticket.id);
    if (ticket?.reserved_seat) {
      setSeatsModalIsVisible(true);
    } else {
      removeTicket(ticket);
    }
  };

  return (
    <ListContainer>
      {/* Category Tabs */}
      <CategoryTabs>
        {Object.keys(tickets)?.map((key) => (
          <Tab
            key={key}
            selected={selected === key}
            onClick={() => setSelected(key)}
          >
            {tickets[key]?.type?.logo && (
              <TabIcon
                selected={selected === key}
                src={tickets[key]?.type?.logo}
                alt={tickets[key]?.type?.name}
              />
            )}
            {tickets[key]?.type?.name}
          </Tab>
        ))}
      </CategoryTabs>

      {/* Ticket List Header */}
      <TicketListHeader>
        <span>Ticket Type</span>
        <span>Quantity</span>
      </TicketListHeader>

      {/* Ticket Items */}
      <TicketsContainer>
        {tickets[selected]?.tickets?.map((ticket) => (
          <TicketItem key={ticket.id}>
            <TicketName>{ticket?.name}</TicketName>

            {/* Desktop view */}
            <DesktopControls>
              <TicketPrice>{`$${ticket?.price?.toFixed(2)}`}</TicketPrice>
              <QtyControls>
                <POSIconButton
                  variant="ghost"
                  onClick={() => handleRemoveTicket(ticket)}
                >
                  <Icon icon="trash" size={16} color="currentColor" />
                </POSIconButton>
                <QtyDisplay>{ticket?.count || 0}</QtyDisplay>
                <POSIconButton onClick={() => handleAddTicket(ticket)}>
                  +
                </POSIconButton>
              </QtyControls>
            </DesktopControls>

            {/* Mobile view */}
            <MobileRow>
              <TicketPrice>{`$${ticket?.price?.toFixed(2)}`}</TicketPrice>
              <QtyControls>
                <POSIconButton
                  variant="ghost"
                  onClick={() => handleRemoveTicket(ticket)}
                >
                  <Icon icon="trash" size={16} color="currentColor" />
                </POSIconButton>
                <QtyDisplay>{ticket?.count || 0}</QtyDisplay>
                <POSIconButton onClick={() => handleAddTicket(ticket)}>
                  +
                </POSIconButton>
              </QtyControls>
            </MobileRow>
          </TicketItem>
        ))}
      </TicketsContainer>

      {/* Seats Manager Modal */}
      <SeatsManagerModal
        isVisible={seatsModalIsVisible}
        setIsVisible={setSeatsModalIsVisible}
      />
    </ListContainer>
  );
};
